import { ROUTES } from '#/config/routes';
import {
  rememberAsyncFilterLabels,
  resolveFromLabelCache,
} from '#/modules/commonComponents/table/asyncFilterLabelCache';
import {
  createAmountRangeFilterMeta,
  createAsyncMultiFilterMeta,
  createFixedMultiFilterMeta,
  createTextFilterMeta,
} from '#/modules/commonComponents/table/columnFilterMeta';
import TruncatedTextWithTooltip from '#/modules/commonComponents/table/TruncatedTextWithTooltip';
import { createDocumentNavLinksColumn } from '#/modules/commonComponents/table/tableColumnHelpers';
import PaymentRequestStatusBadge from '#/modules/finMgmt/claims/components/PaymentRequestStatusBadge';
import {
  canEditPaymentRequest,
  formatPaymentRequestStatusLabel,
  PAYMENT_REQUEST_STATUS_FILTER_OPTIONS,
} from '#/modules/finMgmt/claims/helper/paymentRequestStatus';
import { FMS_READ_SCOPE } from '#/api/fmsMgmt/fmsReadScope';
import { createPayeeFilterOptionsSearch } from '#/modules/finMgmt/claims/helper/searchPayeeFilterOptions';
import { formatDateTimeOrDash } from '#/utils/date';
import { formatFmsAmount } from '#/modules/finMgmt/shared/helper/fmsAmount';

function displayDash(value) {
  if (value === null || value === undefined || value === '') {
    return '—';
  }
  return value;
}

/**
 * @param {object} options
 * @param {'personal'|'finance'} [options.mode] personal＝個人中心；finance＝財務管理（多領款人、備註欄、無編輯連結）
 * @param {(row: object) => boolean} [options.canShowEdit] personal 才用
 * @param {Map<string, string>} [options.payeeLabelCache] finance 領款人 filter chip label
 * @param {(range: { min?: string, max?: string }) => void} [options.onTotalAmountRangeChange] 總額區間（含端點）
 */
export function buildPaymentRequestColumns({
  filters = {},
  onFilterChange,
  mode = 'personal',
  canShowEdit = (row) => canEditPaymentRequest(row.status),
  payeeLabelCache,
  onTotalAmountRangeChange,
} = {}) {
  const isFinance = mode === 'finance';
  const getDetailPath = (row) => (
    isFinance
      ? ROUTES.fmsPaymentRequestDetail(row.id)
      : ROUTES.personalCenterClaimDetail(row.id)
  );

  const columns = [
    createDocumentNavLinksColumn({
      getDetailPath,
      getEditPath: (row) => ROUTES.personalCenterClaimEdit(row.id),
      canShowEdit: isFinance ? () => false : canShowEdit,
    }),
    {
      id: 'requestNo',
      header: '請款單號',
      accessorKey: 'requestNo',
      accessorFn: (row) => displayDash(row.requestNo),
      size: 140,
      enableColumnFilter: true,
      meta: {
        cellClassName: 'td_left',
        ...createTextFilterMeta({
          key: 'requestNo',
          label: '請款單號',
          filterValue: filters.requestNo,
          onChange: (value) => onFilterChange('requestNo', value),
          placeholder: '搜尋請款單號',
        }),
      },
      cell: ({ row }) => (
        <TruncatedTextWithTooltip
          text={displayDash(row.original.requestNo)}
          detailPath={getDetailPath(row.original)}
          tooltipContent="雙擊開啟請款單"
        />
      ),
    },
    {
      id: 'status',
      header: '狀態',
      accessorKey: 'status',
      accessorFn: (row) => formatPaymentRequestStatusLabel(row.status),
      size: 120,
      enableColumnFilter: true,
      meta: {
        statusKind: 'document',
        ...createFixedMultiFilterMeta({
          key: 'status',
          label: '狀態',
          options: PAYMENT_REQUEST_STATUS_FILTER_OPTIONS,
          filterValue: filters.status,
          onChange: (value) => onFilterChange('status', value),
        }),
      },
      cell: ({ row }) => (
        row.original.status
          ? <PaymentRequestStatusBadge status={row.original.status} size="sm" />
          : '—'
      ),
    },
    ...(isFinance ? [{
      id: 'payee',
      header: '領款人',
      accessorFn: (row) => displayDash(row.payee?.name || row.payee?.accountNo),
      size: 130,
      enableSorting: false,
      enableColumnFilter: true,
      meta: {
        cellClassName: 'td_left',
        // 選項走 `/fms/payment-requests/payees`（`.read`）；payee_id 逗號＝OR
        ...createAsyncMultiFilterMeta({
          key: 'payeeId',
          label: '領款人',
          searchOptions: createPayeeFilterOptionsSearch(FMS_READ_SCOPE.ALL),
          filterValue: filters.payeeId,
          onChange: (value) => onFilterChange('payeeId', value),
          searchPlaceholder: '搜尋帳號或姓名',
          resolveFilterDisplay: (values) => resolveFromLabelCache(payeeLabelCache, values),
          rememberSelectedOptions: (options, values) => {
            rememberAsyncFilterLabels(payeeLabelCache, options, values);
          },
        }),
      },
    }] : []),
    {
      id: 'totalAmount',
      header: '總額',
      accessorKey: 'totalAmount',
      accessorFn: (row) => formatFmsAmount(row.totalAmount),
      size: 120,
      enableColumnFilter: Boolean(onTotalAmountRangeChange),
      meta: {
        cellClassName: 'td_right',
        ...(onTotalAmountRangeChange ? createAmountRangeFilterMeta({
          label: '總額',
          min: filters.totalAmountMin,
          max: filters.totalAmountMax,
          onChange: onTotalAmountRangeChange,
        }) : {}),
      },
    },
    {
      id: 'updatedAt',
      header: '更新時間',
      accessorKey: 'updatedAt',
      accessorFn: (row) => formatDateTimeOrDash(row.updatedAt),
      size: 160,
    },
    {
      id: 'paymentReason',
      header: '事由',
      accessorKey: 'paymentReason',
      accessorFn: (row) => displayDash(row.paymentReason),
      size: 200,
      meta: { cellClassName: 'td_left' },
      cell: ({ row }) => (
        <TruncatedTextWithTooltip text={displayDash(row.original.paymentReason)} />
      ),
    },
    ...(isFinance ? [{
      id: 'remark',
      header: '備註',
      accessorFn: (row) => displayDash(row.remark),
      size: 200,
      enableSorting: false,
      meta: { cellClassName: 'td_left' },
      cell: ({ row }) => <TruncatedTextWithTooltip text={displayDash(row.original.remark)} />,
    }] : []),
  ];

  return columns;
}

export const paymentRequestSideMsgBlocks = [
  {
    fields: [
      { label: '請款單號', value: 'requestNo' },
      { label: '事由', value: 'paymentReason' },
      { label: '總額', value: 'totalAmount' },
      { label: '備註', value: 'remark' },
    ],
  },
];
